import {
  BOARD_COLS,
  BOARD_ROWS,
  INITIAL_ENEMY_PIECES,
  INITIAL_PLAYER_PIECES,
  PLAYER_BOTTOM_ROW,
  STANDARD_PIECE_CAPS,
} from "./constants";
import type { Piece, PieceType, Side, Square } from "./types";
import { randInt, shuffle } from "./rng";

const INITIAL_RANDOM_TYPES: PieceType[] = ["pawn", "pawn", "pawn", "knight", "knight", "bishop", "rook", "queen"];
const SPAWN_RANDOM_TYPES: PieceType[] = ["pawn", "pawn", "pawn", "knight", "knight", "bishop", "rook", "queen"];

type PieceCounts = Record<PieceType, number>;

function emptyPieceCounts(): PieceCounts {
  return {
    pawn: 0,
    knight: 0,
    bishop: 0,
    rook: 0,
    queen: 0,
    king: 0,
  };
}

function pickWithinCaps(
  rng: () => number,
  weightedTypes: readonly PieceType[],
  counts: PieceCounts,
): PieceType | null {
  const eligible = weightedTypes.filter((type) => counts[type] < STANDARD_PIECE_CAPS[type]);
  if (eligible.length === 0) return null;
  return eligible[randInt(rng, eligible.length)];
}

function makeInitialTypePlan(rng: () => number, count: number): PieceType[] {
  if (count <= 0) return [];
  const counts = emptyPieceCounts();
  const out: PieceType[] = ["king"];
  counts.king = 1;
  while (out.length < count) {
    const next = pickWithinCaps(rng, INITIAL_RANDOM_TYPES, counts);
    if (!next) break;
    out.push(next);
    counts[next] += 1;
  }
  return out;
}

export class Board {
  /** grid[row][col] - row 0 is top (enemy side), row BOARD_ROWS-1 is bottom (player side). */
  private grid: (Piece | null)[][];
  private nextId = 1;

  constructor() {
    this.grid = Array.from({ length: BOARD_ROWS }, () => Array<Piece | null>(BOARD_COLS).fill(null));
  }

  static random(rng: () => number): Board {
    const b = new Board();
    const allSquares: Square[] = [];
    for (let r = 0; r < BOARD_ROWS; r++) {
      for (let c = 0; c < BOARD_COLS; c++) allSquares.push({ row: r, col: c });
    }

    // Puzzle-like start: players can appear anywhere, but enemies never start on
    // the player's back rank — they must never be able to block a wrap.
    const shuffledSquares = shuffle(rng, allSquares);
    const playerCount = Math.min(INITIAL_PLAYER_PIECES, shuffledSquares.length);

    const shuffledPlayer = shuffledSquares.slice(0, playerCount);
    const playerTypes = makeInitialTypePlan(rng, playerCount);
    // Player pawns must not spawn on the top rank (row 0) — they'd be stuck with no forward move.
    const pawnSqs = shuffledPlayer.filter((sq) => sq.row !== 0);
    const nonPawnSqs = shuffledPlayer.filter((sq) => sq.row === 0);
    for (let pi = 0, ni = 0, i = 0; i < playerTypes.length; i++) {
      if (playerTypes[i] === "pawn") {
        b.place(pawnSqs[pi++], b.makePiece("pawn", "player"));
      } else {
        b.place(nonPawnSqs[ni] ? nonPawnSqs[ni++] : pawnSqs[pi++], b.makePiece(playerTypes[i], "player"));
      }
    }

    const enemySquares = shuffledSquares
      .slice(playerCount)
      .filter((sq) => sq.row !== PLAYER_BOTTOM_ROW);
    const enemyCount = Math.min(INITIAL_ENEMY_PIECES, enemySquares.length);
    const shuffledEnemy = enemySquares.slice(0, enemyCount);
    const enemyTypes = makeInitialTypePlan(rng, enemyCount);
    for (let i = 0; i < enemyTypes.length; i++) {
      b.place(shuffledEnemy[i], b.makePiece(enemyTypes[i], "enemy"));
    }
    return b;
  }

  makePiece(type: PieceType, side: Side): Piece {
    return { id: this.nextId++, type, side };
  }

  get(sq: Square): Piece | null {
    if (!this.inBounds(sq)) return null;
    return this.grid[sq.row][sq.col];
  }

  place(sq: Square, piece: Piece | null): void {
    this.grid[sq.row][sq.col] = piece;
  }

  inBounds(sq: Square): boolean {
    return sq.row >= 0 && sq.row < BOARD_ROWS && sq.col >= 0 && sq.col < BOARD_COLS;
  }

  /** Returns true if the row contains no pieces. */
  rowIsEmpty(row: number): boolean {
    for (let c = 0; c < BOARD_COLS; c++) {
      if (this.grid[row][c] !== null) return false;
    }
    return true;
  }

  countPieces(side: Side): number {
    let n = 0;
    for (let r = 0; r < BOARD_ROWS; r++) {
      for (let c = 0; c < BOARD_COLS; c++) {
        const p = this.grid[r][c];
        if (p && p.side === side) n++;
      }
    }
    return n;
  }

  /** Iterate squares occupied by pieces of a given side. */
  *squaresOf(side: Side): Generator<{ sq: Square; piece: Piece }> {
    for (let r = 0; r < BOARD_ROWS; r++) {
      for (let c = 0; c < BOARD_COLS; c++) {
        const p = this.grid[r][c];
        if (p && p.side === side) yield { sq: { row: r, col: c }, piece: p };
      }
    }
  }

  emptySquaresInRow(row: number): Square[] {
    const out: Square[] = [];
    for (let c = 0; c < BOARD_COLS; c++) {
      if (this.grid[row][c] === null) out.push({ row, col: c });
    }
    return out;
  }

  /**
   * Shift the entire grid down by one row: bottom row is discarded,
   * a new empty row appears at the top. Used when the player-side bottom row is empty.
   * Enemy pawns that reach the bottom rank via shift are teleported back to the
   * top rank (same column) so they never block future wraps.
   */
  shiftDownOne(): void {
    for (let r = BOARD_ROWS - 1; r > 0; r--) {
      this.grid[r] = this.grid[r - 1];
    }
    this.grid[0] = Array<Piece | null>(BOARD_COLS).fill(null);
    const bottomRank = BOARD_ROWS - 1;
    for (let c = 0; c < BOARD_COLS; c++) {
      const p = this.grid[bottomRank][c];
      if (p && p.side === "enemy" && p.type === "pawn") {
        this.grid[0][c] = p;
        this.grid[bottomRank][c] = null;
      }
    }
  }

  private countByType(side: Side): PieceCounts {
    const counts = emptyPieceCounts();
    for (let r = 0; r < BOARD_ROWS; r++) {
      for (let c = 0; c < BOARD_COLS; c++) {
        const p = this.grid[r][c];
        if (!p || p.side !== side) continue;
        counts[p.type] += 1;
      }
    }
    return counts;
  }

  /** All empty squares outside the player's back rank; enemies never spawn there
   *  so they can't permanently block a wrap. */
  private spawnableSquares(): Square[] {
    const out: Square[] = [];
    for (let r = 0; r < BOARD_ROWS - 1; r++) {
      for (let c = 0; c < BOARD_COLS; c++) {
        if (this.grid[r][c] === null) out.push({ row: r, col: c });
      }
    }
    return out;
  }

  /** Spawn up to N random enemy pieces on empty squares anywhere but the back rank. */
  spawnEnemiesAnywhere(rng: () => number, count: number): Piece[] {
    const empties = this.spawnableSquares();
    const picks = shuffle(rng, empties).slice(0, Math.min(count, empties.length));
    const enemyCounts = this.countByType("enemy");
    const created: Piece[] = [];
    for (const sq of picks) {
      // No king spawning during run.
      const type = pickWithinCaps(rng, SPAWN_RANDOM_TYPES, enemyCounts);
      if (!type) break;
      const piece = this.makePiece(type, "enemy");
      piece.summonSick = true;
      this.place(sq, piece);
      enemyCounts[type] += 1;
      created.push(piece);
    }
    return created;
  }

  /** Wake up all pieces with summoning sickness so they can capture again. */
  clearSummonSickness(): void {
    for (let r = 0; r < BOARD_ROWS; r++) {
      for (let c = 0; c < BOARD_COLS; c++) {
        const p = this.grid[r][c];
        if (p?.summonSick) delete p.summonSick;
      }
    }
  }
}
